import React, { useCallback, useMemo, useEffect, useState, memo } from 'react';
import { Upload } from 'lucide-react';
import AgentLogo from '../../common/logo/AgentLogo';
import Button from '../../common/button/Button';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import { FormInput, FormSelect, FormLabel, FormGroup, HelpText, FormSuggestions, WarningBox } from '../../common/form';
import { AI_MODELS } from '../../../constants/models';
import { useNavigate } from 'react-router-dom';
import { deleteAgent, getAgentChats } from '../../../api/agents/agents';

import sharedStyles from '../styles/AgentSettings.module.css';
import styles from './styles/AgentGeneralTab.module.css';

// Character limits for text fields
const NAME_MAX = 80;
const DESCRIPTION_MAX = 500;
const SYSTEM_PROMPT_MAX = 8000;
const WELCOME_MESSAGE_MAX = 300;

const AgentGeneralTab = ({ data, onChange }) => {
    const navigate = useNavigate();
    const [hasChats, setHasChats] = useState(true);
    const [checkingChats, setCheckingChats] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const modelOptions = useMemo(() => {
        const options = [];
        Object.entries(AI_MODELS).forEach(([key, provider]) => {
            provider.models.forEach(model => {
                options.push({
                    value: model.id,
                    label: model.label + ' (' + provider.name + ')',
                });
            });
        });
        return options;
    }, []);

    const handleChange = useCallback((field, value) => {
        // Prevent leading spaces for text fields
        if (typeof value === 'string' && field !== 'avatar') {
            value = value.replace(/^\s+/, '');
        }
        onChange({ [field]: value });
    }, [onChange]);

    const handleFileUpload = useCallback(e => {
        const file = e.target.files[0];
        if (file) {
            // TODO: Implement file upload
            console.log('File selected:', file);
            // For now, just create a local URL
            const fileUrl = URL.createObjectURL(file);
            handleChange('avatar', fileUrl);
        }
    }, [handleChange]);

    const handleUploadClick = useCallback(() => {
        document.getElementById('avatar-upload').click();
    }, []);

    // Hide delete block if agent has chats
    useEffect(() => {
        const checkChats = async () => {
            if (!data?.slug) {
                setHasChats(true);
                return;
            }
            try {
                setCheckingChats(true);
                const result = await getAgentChats(data.slug, 1, 1);
                const any = Array.isArray(result?.chats) && result.chats.length > 0;
                setHasChats(any);
            } catch (err) {
                setHasChats(true);
            } finally {
                setCheckingChats(false);
            }
        };
        checkChats();
    }, [data?.slug]);

    const handleDelete = useCallback(async () => {
        if (!data?.slug) return;
        try {
            setDeleteError(null);
            await deleteAgent(data.slug);
            navigate('/agents');
        } catch (err) {
            setDeleteError(err?.message || 'Failed to delete agent');
        }
    }, [data?.slug, navigate]);

    return (
        <div className={sharedStyles.tabContent}>
            <div className={sharedStyles.section}>
                <h3 className={sharedStyles.sectionTitle}>Basic Information</h3>
                <p className={sharedStyles.sectionDescription}>
                    Configure the basic details of your agent that users will see.
                </p>

                <FormGroup>
                    <FormLabel>Agent Avatar</FormLabel>
                    <div className={styles.avatarUpload}>
                        <AgentLogo
                            logo={data.avatar}
                            name={data.name}
                            size="xlarge"
                            className={styles.avatarPreview}
                        />
                        <Button
                            onClick={handleUploadClick}
                            variant="outline"
                            size="small"
                        >
                            <Upload size={16} />
                            Upload Image
                        </Button>
                        <input
                            type="file"
                            id="avatar-upload"
                            accept="image/*"
                            onChange={handleFileUpload}
                            style={{ display: 'none' }}
                        />
                    </div>
                    <HelpText>Recommended size: 200x200px</HelpText>
                </FormGroup>

                <div className={sharedStyles.formGrid}>
                    <FormGroup>
                        <FormLabel htmlFor="name" required>
                            Agent Name
                        </FormLabel>
                        <FormInput
                            id="name"
                            value={data.name}
                            onChange={value => handleChange('name', value)}
                            placeholder="Enter agent name"
                            maxLength={NAME_MAX}
                        />
                        <div className={sharedStyles.helpRow}>
                            <HelpText>The display name for your agent</HelpText>
                            <HelpText className={sharedStyles.helpTextRight}>
                                {(data.name || '').length}/{NAME_MAX}
                            </HelpText>
                        </div>
                    </FormGroup>

                    <FormGroup>
                        <FormLabel htmlFor="slug" required>
                            Agent Slug
                        </FormLabel>
                        <FormInput
                            id="slug"
                            value={data.slug}
                            onChange={value => handleChange('slug', value.toLowerCase())}
                            placeholder="unique_agent_identifier"
                            disabled={true}
                        />
                        <HelpText>
                            Unique agent identifier. Can only be changed through support.
                        </HelpText>
                    </FormGroup>
                </div>

                <FormGroup>
                    <FormLabel htmlFor="description">
                        Description
                    </FormLabel>
                    <FormInput
                        type="textarea"
                        id="description"
                        value={data.description}
                        onChange={value => handleChange('description', value)}
                        rows={3}
                        placeholder="Describe what your agent does..."
                        maxLength={DESCRIPTION_MAX}
                    />
                    <div className={sharedStyles.helpRow}>
                        <HelpText>
                            A brief description of your agent's capabilities
                        </HelpText>
                        <HelpText className={sharedStyles.helpTextRight}>
                            {(data.description || '').length}/{DESCRIPTION_MAX}
                        </HelpText>
                    </div>
                </FormGroup>
            </div>

            <div className={sharedStyles.section}>
                <h3 className={sharedStyles.sectionTitle}>Privacy</h3>
                <p className={sharedStyles.sectionDescription}>
                    Choose who can chat with your agent.
                </p>

                <FormGroup>
                    <FormLabel htmlFor="accessType">Privacy Mode</FormLabel>
                    <FormSelect
                        id="accessType"
                        value={data.accessType}
                        onChange={value => handleChange('accessType', value)}
                        options={[
                            { value: 'PRIVATE', label: 'Private (owner only)' },
                            { value: 'REGISTERED', label: 'Registered users' },
                            { value: 'PUBLIC', label: 'Public (no login required)' },
                            // { value: 'LINK', label: 'By link (token)' },
                        ]}
                        placeholder="Select privacy"
                    />
                    {data.accessType === 'PUBLIC' && (
                        <WarningBox>
                            <p>
                                Public mode is enabled. Any user can send messages to this agent. Usage costs (credits) will be charged to the agent owner.
                            </p>
                        </WarningBox>
                    )}
                </FormGroup>

                {data.accessType === 'REGISTERED' && (
                    <FormGroup>
                        <FormLabel htmlFor="creditsPayer">Credits Billing</FormLabel>
                        <FormSelect
                            id="creditsPayer"
                            value={data.creditsPayer || 'OWNER'}
                            onChange={value => handleChange('creditsPayer', value)}
                            options={[
                                { value: 'OWNER', label: 'Charge agent owner' },
                                { value: 'USER', label: 'Charge registered user' },
                            ]}
                            placeholder="Select who pays credits"
                        />
                        <div className={sharedStyles.helpRow}>
                            <HelpText>
                                Select who will be charged for usage when registered users chat with this agent.
                            </HelpText>
                            <HelpText className={sharedStyles.helpTextRight}>
                                Applies only to registered access
                            </HelpText>
                        </div>
                    </FormGroup>
                )}
            </div>

            <div className={sharedStyles.section}>
                <h3 className={sharedStyles.sectionTitle}>Agent Behavior</h3>
                <p className={sharedStyles.sectionDescription}>
                    Configure how your agent interacts with users and processes requests.
                </p>

                <FormGroup>
                    <FormLabel htmlFor="model">AI Model</FormLabel>
                    <FormSelect
                        id="model"
                        value={data.model}
                        onChange={value => handleChange('model', value)}
                        options={modelOptions}
                        placeholder="Select AI model"
                    />
                    <HelpText>
                        The AI model used to generate agent's final responses. Models used to execute tools are selected by us and cannot be configured.
                    </HelpText>
                </FormGroup>

                <FormGroup>
                    <FormLabel htmlFor="systemPrompt">
                        System Prompt
                    </FormLabel>
                    <FormInput
                        type="textarea"
                        id="systemPrompt"
                        value={data.systemPrompt}
                        onChange={value => handleChange('systemPrompt', value)}
                        rows={5}
                        placeholder="You are a helpful assistant..."
                        maxLength={SYSTEM_PROMPT_MAX}
                    />
                    <div className={sharedStyles.helpRow}>
                        <HelpText>
                            Instructions that define your agent's personality and behavior
                        </HelpText>
                        <HelpText className={sharedStyles.helpTextRight}>
                            {(data.systemPrompt || '').length}/{SYSTEM_PROMPT_MAX}
                        </HelpText>
                    </div>
                </FormGroup>

                <FormGroup>
                    <FormLabel htmlFor="welcomeMessage">
                        Welcome Message
                    </FormLabel>
                    <FormInput
                        type="textarea"
                        id="welcomeMessage"
                        value={data.welcomeMessage}
                        onChange={value => handleChange('welcomeMessage', value)}
                        rows={3}
                        placeholder="Hello! How can I help you today?"
                        maxLength={WELCOME_MESSAGE_MAX}
                    />
                    <div className={sharedStyles.helpRow}>
                        <HelpText>
                            The first message users see when starting a conversation
                        </HelpText>
                        <HelpText className={sharedStyles.helpTextRight}>
                            {(data.welcomeMessage || '').length}/{WELCOME_MESSAGE_MAX}
                        </HelpText>
                    </div>
                    <FormSuggestions
                        suggestions={[
                            'Hello! How can I help you today?',
                            'Hi there! What can I do for you?',
                            'Welcome! Tell me what you need and I will assist.',
                            'Hi! Ask me anything about this agent’s domain.',
                        ]}
                        onSelect={val => handleChange('welcomeMessage', val)}
                        ariaLabel="Welcome message suggestions"
                    />
                </FormGroup>
            </div>

            {!hasChats && (
                <div className={sharedStyles.section}>
                    <h3 className={sharedStyles.sectionTitle}>Danger Zone</h3>
                    <p className={sharedStyles.sectionDescription}>
                        You can permanently delete this agent only if it has no chats.
                    </p>
                    {deleteError && (
                        <p style={{ color: 'var(--color-red, #d00)', marginTop: '0.5rem' }}>{deleteError}</p>
                    )}
                    <Button
                        variant="danger"
                        size="small"
                        onClick={() => setShowDeleteConfirmation(true)}
                        disabled={checkingChats}
                        title="Delete this agent"
                    >
                        Delete Agent
                    </Button>
                </div>
            )}

            <ConfirmationDialog
                isOpen={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                onConfirm={handleDelete}
                title="Delete Agent"
                message="This action cannot be undone. The agent will be permanently deleted."
                confirmText="Delete"
                cancelText="Cancel"
                dangerous={true}
            />
        </div>
    );
};

export default memo(AgentGeneralTab);
